// LP の写真タブ（reservation_lp/photos・jpe_reservation_lp/photos）が読む JS（#2311）。
// reservation_lp.ts と同じく ESM でビルドし、<script type="module"> で読む（理由は client/reservation_lp.ts の冒頭）。
document.addEventListener('turbo:load', function () {
  // .masonry-grid の data-photos の 1 件・測った画像のサイズ・ギャラリーの並び（1 マス）
  type Photo = { url: string; caption: string }
  type ImageSize = { width: number; height: number; index: number }
  type LayoutItem = { type: string; image?: number | null; content?: LayoutItem[] }

  // Get image size
  async function getImageSize(url: string) {
    try {
      const img = new Image()
      return new Promise<{ width: number; height: number }>((resolve, reject) => {
        img.onload = () => resolve({ width: img.width, height: img.height })
        img.onerror = () => reject(new Error(`Failed to load image: ${url}`))
        img.src = url
      })
    } catch (error) {
      return { width: 720, height: 720 }
    }
  }

  function createLayoutFromSizes(sizes: ImageSize[]) {
    const numImages = sizes.length
    const layout: LayoutItem[] = []
    // aspect ratio = height / width
    const ars = sizes.map((s) => (s.width > 0 ? s.height / s.width : 1))

    // thresholds
    const tallThreshold = 1.3
    const wideThreshold = 0.8
    const tolerance = 0.14
    const maxStack = 4

    const idx = (k: number) => (sizes[k] ? sizes[k].index : null)

    function widthsEqual(a: ImageSize | undefined, b: ImageSize | undefined, eps = 0.05) {
      if (!a || !b) return false
      return Math.abs(a.width - b.width) / Math.max(1, a.width) <= eps
    }

    function allWidthsEqual(arr: ImageSize[], eps = 0.05) {
      if (!arr.length) return false
      const firstW = arr[0].width
      return arr.every((a) => Math.abs(a.width - firstW) / firstW <= eps)
    }

    function pushFullAvoidConsecutive(imageIndex: number | null) {
      if (!layout.length) {
        layout.push({ type: 'full', image: imageIndex })
        return
      }
      const last = layout[layout.length - 1]
      if (last && last.type === 'full' && typeof last.image !== 'undefined' && last.image !== null) {
        const prevImg = last.image
        if (widthsEqual(sizes[prevImg], sizes[imageIndex as number])) {
          layout.pop()
          const prevAr = ars[prevImg]
          const currAr = ars[imageIndex as number]
          const itemType = prevAr <= wideThreshold && currAr <= wideThreshold ? 'half' : 'row'
          layout.push({
            type: 'full d-flex-row',
            content: [
              { type: itemType, image: prevImg },
              { type: itemType, image: imageIndex },
            ],
          })
        } else {
          layout.push({ type: 'full', image: imageIndex })
        }
      } else {
        layout.push({ type: 'full', image: imageIndex })
      }
    }

    // SPECIAL CASE 1: 3 images, same width and all are tall
    const allTall = ars.every((r) => r >= tallThreshold)
    if (numImages === 3 && allWidthsEqual(sizes) && allTall) {
      layout.push({
        type: 'full d-flex-row',
        content: [
          { type: 'half tall', image: idx(0) },
          {
            type: 'half vertical',
            content: [
              { type: 'stacked-item', image: idx(1) },
              { type: 'stacked-item', image: idx(2) },
            ],
          },
        ],
      })
      return layout
    }

    // SPECIAL CASE 2: 3 images, same width and all are wide
    const allWide = ars.every((r) => r <= wideThreshold)
    if (numImages === 3 && allWide) {
      // Row 1: first image full width
      layout.push({ type: 'full', image: idx(0) })
      // Row 2: second and third image each half
      layout.push({
        type: 'full d-flex-row',
        content: [
          { type: 'half', image: idx(1) },
          { type: 'half', image: idx(2) },
        ],
      })
      return layout
    }

    // SPECIAL CASE 3: >= 2 images, same width and all are tall
    const allSameWidth = sizes.every((s) => s.width === sizes[0].width)
    if (allSameWidth) {
      for (let i = 0; i < numImages; i += 2) {
        if (i + 1 < numImages) {
          layout.push({
            type: 'full d-flex-row',
            content: [
              { type: 'row', image: idx(i) },
              { type: 'row', image: idx(i + 1) },
            ],
          })
        } else {
          pushFullAvoidConsecutive(idx(i))
        }
      }
      return layout
    }

    // --- MAIN LOOP ---
    let i = 0
    while (i < numImages) {
      const ar = ars[i]

      // NEW SPECIAL CASE for: small, small, small, tall
      if (i + 3 < numImages && allWidthsEqual(sizes.slice(i, i + 3)) && ars[i + 3] >= tallThreshold) {
        const prevArs = ars.slice(i, i + 3)
        const sumBefore = prevArs.reduce((s, x) => s + x, 0)
        const tallAr = ars[i + 3]
        if (sumBefore >= tallAr * (1 - tolerance) && sumBefore <= tallAr * (1 + tolerance)) {
          layout.push({
            type: 'full d-flex-row',
            content: [
              {
                type: 'half vertical',
                content: prevArs.map((_, j) => ({
                  type: 'stacked-item',
                  image: idx(i + j),
                })),
              },
              { type: 'half tall', image: idx(i + 3) },
            ],
          })
          i += 4
          continue
        }
      }

      // SPECIAL CASE for: small, small, tall
      if (i + 2 < numImages && widthsEqual(sizes[i], sizes[i + 1]) && ars[i + 2] >= tallThreshold) {
        const sum = ars[i] + ars[i + 1]
        const tallAr = ars[i + 2]
        if (sum >= tallAr * (1 - tolerance) && sum <= tallAr * (1 + tolerance)) {
          layout.push({
            type: 'full d-flex-row',
            content: [
              {
                type: 'half vertical',
                content: [
                  { type: 'stacked-item', image: idx(i) },
                  { type: 'stacked-item', image: idx(i + 1) },
                ],
              },
              { type: 'half tall', image: idx(i + 2) },
            ],
          })
          i += 3
          continue
        }
      }

      // 1) Tall
      if (ar >= tallThreshold) {
        // If not found before, find after
        let foundAfter = false
        for (let k = 1; k <= maxStack && i + k < numImages; k++) {
          const sum = ars.slice(i + 1, i + 1 + k).reduce((s, x) => s + x, 0)
          if (sum >= ar * (1 - tolerance) && sum <= ar * (1 + tolerance)) {
            layout.push({
              type: 'full d-flex-row',
              content: [
                { type: 'half tall', image: idx(i) },
                {
                  type: 'half vertical',
                  content: ars.slice(i + 1, i + 1 + k).map((_, j) => ({
                    type: 'stacked-item',
                    image: idx(i + 1 + j),
                  })),
                },
              ],
            })
            i = i + 1 + k
            foundAfter = true
            break
          }
        }
        if (!foundAfter) {
          if (i + 1 < numImages) {
            layout.push({
              type: 'full d-flex-row',
              content: [
                { type: 'half tall', image: idx(i) },
                { type: 'half', image: idx(i + 1) },
              ],
            })
            i += 2
          } else {
            pushFullAvoidConsecutive(idx(i))
            i += 1
          }
        }

        // 2) Wide
      } else if (ar <= wideThreshold) {
        if (i + 2 < numImages && allWidthsEqual(sizes.slice(i, i + 3)) && ars.slice(i, i + 3).every((r) => r <= wideThreshold)) {
          pushFullAvoidConsecutive(idx(i))
          layout.push({
            type: 'full d-flex-row',
            content: [
              { type: 'half', image: idx(i + 1) },
              { type: 'half', image: idx(i + 2) },
            ],
          })
          i += 3
          continue
        }
        pushFullAvoidConsecutive(idx(i))
        const remainingAfter = numImages - (i + 1)
        if (remainingAfter >= 3 && ars[i + 1] >= tallThreshold && ars[i + 2] >= tallThreshold && ars[i + 3] >= tallThreshold) {
          layout.push({
            type: 'full d-flex-row',
            content: [
              { type: 'half tall', image: idx(i + 1) },
              {
                type: 'half vertical',
                content: [
                  { type: 'stacked-item', image: idx(i + 2) },
                  { type: 'stacked-item', image: idx(i + 3) },
                ],
              },
            ],
          })
          i += 4
          continue
        }
        if (i + 1 < numImages) {
          pushFullAvoidConsecutive(idx(i + 1))
          i += 2
        } else {
          i += 1
        }

        // 3) Normal
      } else {
        if (i + 1 < numImages) {
          layout.push({
            type: 'full d-flex-row',
            content: [
              { type: 'half', image: idx(i) },
              { type: 'half', image: idx(i + 1) },
            ],
          })
          i += 2
        } else {
          pushFullAvoidConsecutive(idx(i))
          i += 1
        }
      }
    }

    return layout
  }

  // Function to create dynamic layout based on number of images and size
  async function createDynamicLayout(images: Photo[]) {
    const sizeResults = await Promise.all(
      images.map(async (imgObj, index) => {
        try {
          const size = await getImageSize(imgObj.url)
          return { width: size.width, height: size.height, index }
        } catch (error) {
          return null
        }
      }),
    )
    const sizes = sizeResults.filter((size) => size !== null)
    return createLayoutFromSizes(sizes)
  }

  // Function to render item recursively
  function renderItem(item: LayoutItem, images: Photo[]): HTMLDivElement {
    const gridItem = document.createElement('div')
    gridItem.classList.add('grid-item')

    if (item.content) {
      gridItem.classList.add(...item.type.split(' '))
      item.content.forEach((subItem) => {
        gridItem.appendChild(renderItem(subItem, images))
      })
    } else {
      gridItem.classList.add(...item.type.split(' '))
      const imageIndex = item.image as number
      const img = document.createElement('img')
      img.src = images[imageIndex].url
      img.alt = `image ${imageIndex + 1}`
      img.onerror = () => {
        img.src = 'https://via.placeholder.com/720x720?text=Error' // Fallback if image error
      }

      const lightboxLink = document.createElement('a')
      lightboxLink.href = images[imageIndex].url
      lightboxLink.dataset.fslightbox = 'gallery'
      lightboxLink.dataset.caption = images[imageIndex].caption
      lightboxLink.appendChild(img)

      gridItem.appendChild(lightboxLink)
    }

    return gridItem
  }

  // Render a gallery
  async function renderGallery(gridElem: HTMLElement, images: Photo[]) {
    try {
      const container = gridElem
      const galleryContainer = document.createElement('div')
      galleryContainer.classList.add('gallery-container')

      const grid = document.createElement('div')
      grid.classList.add('gallery-grid')
      // Create dynamic layout
      const layout = await createDynamicLayout(images)
      // Render each item
      layout.forEach((item) => {
        grid.appendChild(renderItem(item, images))
      })
      galleryContainer.appendChild(grid)
      container.appendChild(galleryContainer)
    } catch (error) {
      console.error(`Error rendering gallery ${gridElem}:`, error)
    }
  }

  // キャプションは、読み込まれた画像の親要素（fslightbox の source のラッパー）に付ける。
  // fslightbox 3.8.0 で instance.elements.sourceAnimationWrappers が無くなり、画像も表示中と前後のスライドの分しか
  // 作られないため、onOpen で全スライドを回すのではなく、画像ごとに呼ばれる onSourceLoad を使う
  function applyLightbox() {
    // fslightbox（client/lp-vendor.ts）が window に置くグローバル
    type FsLightboxWindow = Window & {
      refreshFsLightbox?: () => void
      fsLightboxInstances?: Record<
        string,
        { props: { onSourceLoad?: (instance: unknown, source: HTMLElement, index: number) => void } }
      >
    }
    const fsWindow = window as FsLightboxWindow
    if (typeof fsWindow.refreshFsLightbox !== 'function') return
    fsWindow.refreshFsLightbox()

    // window.fsLightbox は最後に作られたギャラリーしか指さないので、名前で引く
    const lightbox = fsWindow.fsLightboxInstances && fsWindow.fsLightboxInstances.gallery
    if (!lightbox) return

    const allImages = document.querySelectorAll<HTMLElement>('[data-fslightbox="gallery"]')
    lightbox.props.onSourceLoad = function (_instance, source, index) {
      const sourceWrapper = source.parentElement
      if (!sourceWrapper) return
      sourceWrapper.classList.add('lp-lightbox-source')
      const caption = allImages[index] && allImages[index].dataset.caption
      if (!caption || sourceWrapper.querySelector('.fslightbox-caption')) return
      const captionDiv = document.createElement('div')
      captionDiv.className = 'fslightbox-caption'
      captionDiv.textContent = caption
      sourceWrapper.appendChild(captionDiv)
    }
  }

  // Render each gallery
  // 「戻る」で Turbo がキャッシュから復元したページは描画済みのギャラリーを持っているので、描き足さない（写真が二重に並ぶ）
  const renderPromises = Array.from(document.querySelectorAll<HTMLElement>('.masonry-grid'))
    .filter((gridElem) => !gridElem.querySelector('.gallery-container'))
    .map((gridElem) => {
      const images = JSON.parse(gridElem.dataset.photos as string)
      return renderGallery(gridElem, images)
    })

  Promise.all(renderPromises)
    .then(() => {
      const skeleton = document.getElementById('global-skeleton')
      const container = document.getElementById('main-gallery')
      // Remove skeleton
      if (skeleton) {
        skeleton.style.opacity = '0'
        skeleton.style.transition = 'opacity 0.3s ease-out'
        setTimeout(() => skeleton.remove(), 300)
      }
      if (container) container.classList.remove('hidden')
      // Apply lightbox
      applyLightbox()
      setTimeout(() => {
        if (window.setCalendarStickyContainerHeight) {
          window.setCalendarStickyContainerHeight()
        }
      }, 1000)
      return
    })
    .catch(() => {})
})

// Anchor links add active class and scroll to section
document.querySelectorAll<HTMLAnchorElement>('.navi-link').forEach((anchor) => {
  anchor.addEventListener('click', function (e) {
    e.preventDefault()
    const target = document.querySelector(this.getAttribute('href') as string)
    if (target) {
      const targetPosition = target.getBoundingClientRect().top - 60
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth',
      })
    }
  })
})
